import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import api from '../services/api';
import { GuestImageGallery } from './GuestImageGallery';
import { formatCellValue } from '../utils/dateFormatter';
import { getClickableRowStyles, getTableCellStyles, getTableHeaderCellStyles } from '../utils/tableUtils';

// ============================================================================
// Types & Interfaces
// ============================================================================
interface RelatedListConfig {
    id: string;
    tableName: string;
    label: string;
    fields: string[];
    relationshipField: string;
}

interface RelatedListDisplayProps {
    config: RelatedListConfig;
    parentRecordId?: string | number;
    onAddRecord?: () => void;
    onEditRecord?: (recordId: string | number) => void;
}

// ============================================================================
// Component
// ============================================================================
export const RelatedListDisplay: React.FC<RelatedListDisplayProps> = ({
    config,
    parentRecordId,
    onAddRecord,
    onEditRecord
}) => {
    // ============================================================================
    // Hooks & Theme
    // ============================================================================
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // ============================================================================
    // State
    // ============================================================================
    const [relatedRecords, setRelatedRecords] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // ============================================================================
    // Effects
    // ============================================================================
    useEffect(() => {
        if (parentRecordId && config.relationshipField) {
            fetchRelatedRecords();
        }
    }, [parentRecordId, config]);

    // ============================================================================
    // Event Handlers
    // ============================================================================
    const handleRowClick = (record: any) => {
        if (record.id && onEditRecord) {
            onEditRecord(record.id);
        }
    };

    // ============================================================================
    // API Methods
    // ============================================================================
    const fetchRelatedRecords = async () => {
        if (!parentRecordId) return;
        
        setLoading(true);
        setError(null);
        
        try {
            const requestForId = parentRecordId; // capture the id this fetch is for
            const params = new URLSearchParams({
                [`filter[${config.relationshipField}]`]: requestForId.toString()
            });
            const url = `/${config.tableName}?${params}`;
            const response = await api.get(url);

            // Ignore stale responses – only update if parentRecordId is unchanged
            if (requestForId !== parentRecordId) {
                return;
            }

            const data = response.data;
            setRelatedRecords(Array.isArray(data) ? data : []);
        } catch (err) {
            setError('Failed to load related records');
        } finally {
            setLoading(false);
        }
    };

    // ============================================================================
    // Special Cases
    // ============================================================================
    // Handle image galleries and cards with specialized components
    if (config.tableName === 'guest_images') {
        return (
            <GuestImageGallery
                guestId={parentRecordId}
                onAddImage={onAddRecord}
            />
        );
    }

    if (config.tableName === 'cards') {
        return (
            <GuestImageGallery
                guestId={parentRecordId}
                onAddImage={onAddRecord}
                apiEndpoint="cards"
                title="Cards Received"
            />
        );
    }

    // ============================================================================
    // Validation
    // ============================================================================
    if (!parentRecordId) {
        return (
            <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {config.label}
                </Typography>
                <Alert severity="info">
                    Save the record first to view related {config.tableName}
                </Alert>
            </Box>
        );
    }

    // ============================================================================
    // Render
    // ============================================================================
    return (
        <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                    {config.label}
                </Typography>
                {onAddRecord && (
                    <Tooltip title={`Add ${config.tableName}`}>
                        <IconButton onClick={onAddRecord} color="primary">
                            <AddIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            ) : error ? (
                <Alert severity="error">{error}</Alert>
            ) : relatedRecords.length === 0 ? (
                <Alert severity="info">
                    No {config.tableName} found
                </Alert>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {config.fields.map((field) => (
                                <TableCell 
                                    key={field}
                                    sx={getTableHeaderCellStyles(isMobile)}
                                >
                                    {field}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {relatedRecords.map((record, index) => (
                            <TableRow 
                                key={record.id || index}
                                hover
                                sx={getClickableRowStyles(theme)}
                                onClick={() => handleRowClick(record)}
                            >
                                {config.fields.map((field) => {
                                    // Format cell value similar to ListView
                                    let cellValue;
                                    try {
                                        const fieldConfig = { 
                                            field_name: field, 
                                            type: undefined 
                                        };
                                        cellValue = formatCellValue(record[field], fieldConfig);
                                        
                                        // Safety check for React rendering
                                        if (cellValue === null || cellValue === undefined) {
                                            cellValue = '-';
                                        } else if (typeof cellValue === 'object') {
                                            cellValue = JSON.stringify(cellValue);
                                        } else {
                                            cellValue = String(cellValue);
                                        }
                                    } catch (error) {
                                        console.error(`[RelatedListDisplay] Error formatting cell value for ${field}:`, error);
                                        cellValue = String(record[field] || '-');
                                    }
                                    
                                    return (
                                        <TableCell 
                                            key={field}
                                            sx={getTableCellStyles(isMobile)}
                                        >
                                            {cellValue}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
}; 